import {
  AppBar,
  Box,
  Container,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Toolbar,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import React, { ReactNode, useState } from 'react'

import DirectionsCarRoundedIcon from '@mui/icons-material/DirectionsCarRounded'
import LocalActivityRoundedIcon from '@mui/icons-material/LocalActivityRounded'
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded'
import { FormattedMessage } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import useAuth from '../hooks/useAuth'
import { MoovyPalette } from '../styles/theme'
import AppMenuTop from './appMenuTop'
import { appRoutes, hasPermission } from './permissions'

const StyledToolbar = styled(Toolbar)({
  background: MoovyPalette.colorMenuBackgroundGradient,
})

const StyledListItemIcon = styled(ListItemIcon)({
  minWidth: 16,
  paddingRight: '10px',
})

const drawerWidth = 280

const Main = styled('main', {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'mobile',
})<{
  open?: boolean
  mobile?: boolean
}>(({ theme, open, mobile }) => ({
  flexGrow: 1,
  paddingTop: 0,
  paddingBottom: 20,
  paddingLeft: 0,
  paddingRight: 0,
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: mobile ? 0 : `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
  backgroundColor: MoovyPalette.colorMainBackground,
  minHeight: '100vh',
  maxHeight: '-webkit-fill-available',
}))

const AppMenu = (props: { children: ReactNode }) => {
  const theme = useTheme()
  const mobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null)
  const [showSideMenu, setShowSideMenu] = useState(mobile ? false : true)
  const { logout, isAuthenticated, isAdmin } = useAuth()

  const navigate = useNavigate()

  const handleOpenUserMenu = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
  ) => {
    setAnchorElUser(event.currentTarget)
  }
  const handleCloseMenu = () => {
    setAnchorElUser(null)
  }

  const closeSideMenu = () => {
    mobile && setShowSideMenu(false)
    return true
  }

  const isValidBasePlusOtherRoute = (appRoute: string) => {
    // The route must be something else than the base route.
    // E.g. /places/0455e4b5-546c-4b21-949c-db7dc87c6c23
    return RegExp(`^${appRoute}/.`).test(location.pathname)
  }

  const menuVisitItems = [
    {
      key: appRoutes.PARKING_EVENTS,
      active:
        location.pathname === appRoutes.PARKING_EVENTS ||
        isValidBasePlusOtherRoute(appRoutes.PARKING_EVENTS),
      onClick: () => {
        closeSideMenu()
        navigate(`${appRoutes.PARKING_EVENTS}`)
      },
      value: <FormattedMessage id="common.text.parkingEvents" />,
      icon: <DirectionsCarRoundedIcon />,
      visible: hasPermission(isAdmin, 'READ', 'PARKING_EVENTS'),
    },
    {
      key: appRoutes.WHITELIST,
      active:
        location.pathname === appRoutes.WHITELIST ||
        isValidBasePlusOtherRoute(appRoutes.WHITELIST),
      onClick: () => {
        closeSideMenu()
        navigate(`${appRoutes.WHITELIST}`)
      },
      value: <FormattedMessage id="common.text.whitelist" />,
      icon: <LocalActivityRoundedIcon />,
      visible: hasPermission(isAdmin, 'READ', 'WHITELIST'),
    },
    {
      key: appRoutes.SITE,
      active: location.pathname === appRoutes.SITE,
      onClick: () => {
        closeSideMenu()
        navigate(`${appRoutes.SITE}`)
      },
      value: <FormattedMessage id="common.text.site" />,
      icon: <PlaceRoundedIcon />,
      visible: hasPermission(isAdmin, 'READ', 'SITE'),
    },
  ]

  return (
    <React.Fragment>
      <Box sx={{ display: 'flex' }}>
        <AppBar
          position="fixed"
          sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
          elevation={0}
        >
          <StyledToolbar>
            <AppMenuTop
              anchorElUser={anchorElUser}
              onOpenUserMenu={handleOpenUserMenu}
              onCloseMenu={handleCloseMenu}
              onLogout={logout}
              onClickHamburgerMenu={() => setShowSideMenu(!showSideMenu)}
              onClickLogo={() => closeSideMenu() && navigate(appRoutes.ROOT)}
              appMenuOpen={showSideMenu}
            />
          </StyledToolbar>
        </AppBar>
        {isAuthenticated && (
          <Drawer
            sx={{
              width: drawerWidth,
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: drawerWidth,
                boxSizing: 'border-box',
                border: 'none',
              },
            }}
            variant={mobile ? undefined : 'persistent'}
            anchor="left"
            open={showSideMenu}
            onClose={() => setShowSideMenu(false)}
          >
            <Toolbar />
            <Box sx={{ overflow: 'auto' }}>
              <List
                subheader={
                  <ListSubheader>
                    {<FormattedMessage id="appMenu.subtitle" />}
                  </ListSubheader>
                }
              >
                {menuVisitItems
                  .filter((item) => item.visible)
                  .map((item) => (
                    <ListItem key={item.key} disablePadding>
                      <ListItemButton
                        onClick={item.onClick}
                        selected={item.active}
                      >
                        <StyledListItemIcon>{item.icon}</StyledListItemIcon>
                        <ListItemText primary={item.value} />
                      </ListItemButton>
                    </ListItem>
                  ))}
              </List>
              <Divider />
            </Box>
          </Drawer>
        )}
        <Main open={showSideMenu} mobile={mobile}>
          <Container>
            <Toolbar />
            <>{props.children}</>
          </Container>
        </Main>
      </Box>
    </React.Fragment>
  )
}

export default AppMenu
